import { ReactNode } from 'react';
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';

import { GachaBoxType, GachaCurrency, GachaRefreshMode, GachaTier } from '../../../../../../__gqltypes__';
import {
  defaultGachaBoxTypeForTier,
  discountedGachaAmount,
  formatUtc,
  GACHA_CURRENCIES,
  GACHA_CURRENCY_LABELS,
  GACHA_TIERS,
  GachaBoxDraft,
} from '../utils';
import { GachaDurationInput } from './GachaDurationInput';
import { GachaUtcDateTime } from './GachaUtcDateTime';

type Props = {
  box: GachaBoxDraft;
  index: number;
  campaignStartDate: number;
  campaignStopDate: number | null;
  onChange: (box: GachaBoxDraft) => void;
  onRemove: () => void;
  // The reward tables and the mystery section, rendered under the basics.
  children?: ReactNode;
};

const ONE_DAY_SECONDS = 86400;

const toInteger = (raw: string): number => {
  const value = Number(raw);
  return Number.isFinite(value) ? Math.floor(value) : 0;
};

/**
 * One box: tier (the type defaults from it), claim limit and refresh, price and discount (PAID), duplicates and
 * the "box ready" reminders. Opening a box is never ad-gated: FREE is a plain claim for everyone.
 */
export function GachaBoxEditor({
  box,
  index,
  campaignStartDate,
  campaignStopDate,
  onChange,
  onRemove,
  children,
}: Props): JSX.Element {
  const update = (patch: Partial<GachaBoxDraft>) => onChange({ ...box, ...patch });
  const idPrefix = `gacha-box-${box._clientId}`;
  const isPaid = box.type === GachaBoxType.PAID;
  const ownDiscountWindow = box.discountStartDate != null || box.discountStopDate != null;

  return (
    <Card className="mb-3 border-primary">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <strong>
          Box {index + 1} — {box.tier}
        </strong>
        <span className="small text-muted">{box.id ? `id ${box.id}` : 'new box (id given on save)'}</span>
        <Button size="sm" variant="outline-danger" onClick={onRemove}>
          <i className="fas fa-trash mr-1" />
          Remove box
        </Button>
      </Card.Header>
      <Card.Body>
        <Form.Group as={Row} className="mb-2">
          <Form.Label column md={3} htmlFor={`${idPrefix}-tier`}>
            Tier
          </Form.Label>
          <Col md={3}>
            <Form.Control
              as="select"
              id={`${idPrefix}-tier`}
              value={box.tier}
              onChange={(e) => {
                const tier = e.target.value as GachaTier;
                update({ tier, type: defaultGachaBoxTypeForTier(tier) });
              }}
            >
              {GACHA_TIERS.map((tier) => (
                <option key={tier} value={tier}>
                  {tier}
                </option>
              ))}
            </Form.Control>
          </Col>
          <Col md={6}>
            <Form.Text className="text-muted">
              The tier sets the box art and its name. FREE and MAESTRO default to a FREE box, the others to PAID.
            </Form.Text>
          </Col>
        </Form.Group>
        {box.tier === GachaTier.MAESTRO && (
          <Alert variant="info" className="py-2">
            A MAESTRO box is shown only to Maestro pack owners, or while a Maestro offer is on sale to the player. A
            non-owner who opens it is sent to the Maestro purchase screen.
          </Alert>
        )}
        <Form.Group as={Row} className="mb-2">
          <Form.Label column md={3} htmlFor={`${idPrefix}-type`}>
            Type
          </Form.Label>
          <Col md={3}>
            <Form.Control
              as="select"
              id={`${idPrefix}-type`}
              value={box.type}
              onChange={(e) => update({ type: e.target.value as GachaBoxType })}
            >
              <option value={GachaBoxType.FREE}>FREE (plain claim, no ad)</option>
              <option value={GachaBoxType.PAID}>PAID</option>
            </Form.Control>
          </Col>
        </Form.Group>

        <h6 className="text-muted text-uppercase small mt-3">Claim limit and refresh (per player)</h6>
        <Form.Group as={Row} className="mb-2">
          <Form.Label column md={3} htmlFor={`${idPrefix}-limit`}>
            Claim limit
          </Form.Label>
          <Col md={2}>
            <Form.Control
              id={`${idPrefix}-limit`}
              type="number"
              min={1}
              step={1}
              value={box.claimLimit}
              onChange={(e) => update({ claimLimit: toInteger(e.target.value) })}
            />
          </Col>
        </Form.Group>
        <Form.Group as={Row} className="mb-2">
          <Form.Label column md={3}>
            Refresh
          </Form.Label>
          <Col md={3} className="pt-2">
            <Form.Check
              type="radio"
              id={`${idPrefix}-refresh-none`}
              name={`${idPrefix}-refresh`}
              label="No reset"
              checked={box.refreshMode === GachaRefreshMode.NONE}
              onChange={() => update({ refreshMode: GachaRefreshMode.NONE })}
            />
            <Form.Check
              type="radio"
              id={`${idPrefix}-refresh-custom`}
              name={`${idPrefix}-refresh`}
              label="Custom"
              checked={box.refreshMode === GachaRefreshMode.CUSTOM}
              onChange={() =>
                update({
                  refreshMode: GachaRefreshMode.CUSTOM,
                  refreshIntervalSeconds: box.refreshIntervalSeconds ?? ONE_DAY_SECONDS,
                })
              }
            />
          </Col>
          {box.refreshMode === GachaRefreshMode.CUSTOM && (
            <Col md={4}>
              <GachaDurationInput
                id={`${idPrefix}-refresh-interval`}
                value={box.refreshIntervalSeconds}
                onChange={(refreshIntervalSeconds) => update({ refreshIntervalSeconds })}
              />
            </Col>
          )}
        </Form.Group>
        <Form.Text className="text-muted mb-2">
          {box.refreshMode === GachaRefreshMode.CUSTOM
            ? 'The full limit comes back this long after the player’s first claim or purchase of the window.'
            : 'Once the limit is reached the player never gets this box again in this campaign.'}
        </Form.Text>

        {isPaid && (
          <>
            <h6 className="text-muted text-uppercase small mt-3">Price</h6>
            <Form.Group as={Row} className="mb-2">
              <Form.Label column md={3} htmlFor={`${idPrefix}-price-amount`}>
                Price
              </Form.Label>
              <Col md={2}>
                <Form.Control
                  id={`${idPrefix}-price-amount`}
                  type="number"
                  min={1}
                  step={1}
                  value={box.priceAmount}
                  onChange={(e) => update({ priceAmount: toInteger(e.target.value) })}
                />
              </Col>
              <Col md={3}>
                <Form.Control
                  as="select"
                  aria-label="Price currency"
                  value={box.priceCurrency}
                  onChange={(e) => update({ priceCurrency: e.target.value as GachaCurrency })}
                >
                  {GACHA_CURRENCIES.map((currency) => (
                    <option key={currency} value={currency}>
                      {GACHA_CURRENCY_LABELS[currency]}
                    </option>
                  ))}
                </Form.Control>
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-2">
              <Col md={{ span: 3, offset: 3 }} className="pt-2">
                <Form.Check
                  type="checkbox"
                  id={`${idPrefix}-discount`}
                  label="Discount"
                  checked={box.discountEnabled}
                  onChange={(e) => update({ discountEnabled: e.target.checked })}
                />
              </Col>
              {box.discountEnabled && (
                <>
                  <Col md={2}>
                    <Form.Control
                      type="number"
                      aria-label="Discount percent"
                      min={1}
                      max={99}
                      step={1}
                      value={box.discountPercent}
                      onChange={(e) => update({ discountPercent: toInteger(e.target.value) })}
                    />
                  </Col>
                  <Col md={4} className="pt-2">
                    % off → players pay{' '}
                    <strong>
                      {discountedGachaAmount(box.priceAmount, box.discountPercent)}{' '}
                      {GACHA_CURRENCY_LABELS[box.priceCurrency]}
                    </strong>
                  </Col>
                </>
              )}
            </Form.Group>
            {box.discountEnabled && (
              <Form.Group as={Row} className="mb-2">
                <Col md={{ span: 9, offset: 3 }}>
                  <Form.Check
                    type="checkbox"
                    id={`${idPrefix}-discount-window`}
                    label="Own discount window (default: the campaign's window)"
                    checked={ownDiscountWindow}
                    onChange={(e) =>
                      update(
                        e.target.checked
                          ? { discountStartDate: campaignStartDate, discountStopDate: campaignStopDate }
                          : { discountStartDate: null, discountStopDate: null }
                      )
                    }
                  />
                  {!ownDiscountWindow && (
                    <Form.Text className="text-muted">
                      The discount runs for the whole campaign
                      {campaignStopDate == null ? ' (a permanent campaign: the discount is permanent).' : '.'}
                    </Form.Text>
                  )}
                  {ownDiscountWindow && (
                    <Row className="mt-2">
                      <Col md={6}>
                        <Form.Label className="small mb-0" htmlFor={`${idPrefix}-discount-start`}>
                          Discount start (UTC)
                        </Form.Label>
                        <GachaUtcDateTime
                          inputId={`${idPrefix}-discount-start`}
                          value={box.discountStartDate}
                          onChange={(discountStartDate) => update({ discountStartDate })}
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Label className="small mb-0" htmlFor={`${idPrefix}-discount-stop`}>
                          Discount end (UTC){box.discountStopDate == null ? ' — empty: until the campaign ends' : ''}
                        </Form.Label>
                        <GachaUtcDateTime
                          inputId={`${idPrefix}-discount-stop`}
                          value={box.discountStopDate}
                          onChange={(discountStopDate) => update({ discountStopDate })}
                        />
                      </Col>
                      <Col md={12}>
                        <Form.Text className="text-muted">
                          Must sit inside the campaign window ({formatUtc(campaignStartDate)} →{' '}
                          {campaignStopDate == null ? 'permanent' : formatUtc(campaignStopDate)}).
                        </Form.Text>
                      </Col>
                    </Row>
                  )}
                </Col>
              </Form.Group>
            )}
          </>
        )}

        <h6 className="text-muted text-uppercase small mt-3">Options</h6>
        <Form.Group as={Row} className="mb-2">
          <Col md={{ span: 9, offset: 3 }}>
            <Form.Check
              type="switch"
              id={`${idPrefix}-duplicates`}
              label="Allow duplicates in one opening (default off)"
              checked={box.allowDuplicates}
              onChange={(e) => update({ allowDuplicates: e.target.checked })}
            />
            <Form.Text className="text-muted">
              Off: a reward already drawn in this opening cannot be drawn again, on any of its rows (drawing 500 Coins
              also removes 5,000 Coins); the remaining weights are renormalised.
            </Form.Text>
            <Form.Check
              type="switch"
              className="mt-2"
              id={`${idPrefix}-reminders`}
              label="Send 'box ready' reminders"
              checked={box.reminderEnabled}
              onChange={(e) => update({ reminderEnabled: e.target.checked })}
            />
            <Form.Text className="text-muted">
              Local reminders when the box can be {isPaid ? 'purchased' : 'claimed'} again (players with &quot;Surprise
              Box reminders&quot; on).
            </Form.Text>
          </Col>
        </Form.Group>

        {children}
      </Card.Body>
    </Card>
  );
}
